<template>
  <div>
    <b-modal
      :id="modalId"
      :size="modalSize"
      no-close-on-backdrop
      no-close-on-esc
      no-fade
      no-stacking
      hide-footer
      centered
      @show="getSubscriptionInformation"
      @hide="() => {}"
    >
      <template #modal-header>
        <div>
          <h2 class="modal-title" data-test-hook="new-subscription-modal-header">
            <div>
              <i class="far fa-badge-dollar colorOrange mr-1" data-test-hook="new-subscription-modal-header-icon" />
              {{ modalTitle }}
            </div>
          </h2>
        </div>
      </template>
      <div v-if="isLoadingSubscriptionData">
        <b-row class="text-center">
          <b-col>
            <b-spinner style="width: 10rem; height: 10rem;" variant="warning" label="Spinning"></b-spinner>
          </b-col>
        </b-row>
      </div>
      <div v-else>
        <AddApparatusUpgradeSubscription
          v-if="activeView === subscriptionViews.UPGRADE_SINGLE_MULTI"
          :isEnrollment="false"
          :currentSubscription="existingSubscription"
          :newSubscription="selectedProduct"
          :apparatusList="apparatusList"
          :new-apparatuses="apparatusesToAddToPlan"
          :isCreatingPurchaseIntent="isCreatingPurchaseIntent"
          @openPayment="proceedToPayment"
        />
        <UpgradeBasicToMulti
          v-if="activeView === subscriptionViews.UPGRADE_BASIC_MULTI"
          :new-subscription="productList"
          :current-subscription="[]"
          :new-apparatuses="apparatusList"
          :apparatus-list="apparatusList"
          @openPayment="proceedToPayment"
          @setSelectedProduct="setSelectedProduct"
        />
        <UpgradeApparatusesExistingSub
          v-if="activeView === subscriptionViews.EXISTING_SUB"
          :current-subscription="existingSubscription"
          :apparatus-list="apparatusList"
          @updateExistingProductApparatuses="updateExistingProductApparatuses"
        />
        <UpgradeFromLegacy
          v-else-if="activeView === subscriptionViews.UPGRADE_LEGACY"
          :accountData="accountProfile"
          :legacySubscriptions="activeLegacySubscriptions"
          :selectedProduct="selectedProduct"
          :annualProduct="productList"
          :apparatusList="apparatusList"
          @setSelectedProduct="setSelectedProduct"
          @openPayment="proceedToPayment"
        />
        <SubscriptionInfoError v-else-if="activeView === subscriptionViews.ERROR" @retry="getSubscriptionInformation" />
      </div>
    </b-modal>
    <PaymentModal
      :modal-id="`${modalId}-payment`"
      :selected-product="selectedProduct"
      :account-profile="accountProfile"
      @submit="payForProduct"
      @goBack="returnToSubscriptions"
      @savePremiumFeaturesGettingReadyInfo="savePremiumFeaturesGettingReadyInfo"
    />

    <TankUtilityEnrollmentModals />
    <EnrollmentModals />

    <Modal modalId="update-apparatuses-spinner-modal" size="modal-lg">
      <ModalBody style="padding-top: 100px; padding-bottom: 100px;">
        <div class="d-flex justify-content-center align-items-center">
          <b-spinner style="width: 10rem; height: 10rem;" variant="warning"></b-spinner>
        </div>
      </ModalBody>
    </Modal>
  </div>
</template>

<script>
import * as Constants from '@/constants'
import UpgradeBasicToMulti from './UpgradeBasicToMulti.vue'
import UpgradeApparatusesExistingSub from './UpgradeApparatusesExistingSub.vue'
import PaymentModal from './PaymentModal.vue'
import SubscriptionInfoError from './SubscriptionInfoError.vue'
import { isFuelMonitorV2 } from '../../../utils/apparatus'
import TankUtilityEnrollmentModals from '../TankUtilityEnrollmentModals.vue'
import EnrollmentModals from '../EnrollmentModals.vue'
import UpgradeFromLegacy from './UpgradeFromLegacy.vue'
import AddApparatusUpgradeSubscription from './AddApparatusUpgradeSubscription.vue'
import {
  isDealerManaged,
  subscriptionActive,
  subscriptionDetailContainsApparatus,
  subscriptionDetailActive
} from '../../../utils/mixins'

const subscriptionViews = {
  NEW_HSB: 'subscription-hsb-new',
  NEW_TANK: 'subscription-tank-new',
  EXISTING_SUB: 'subscription-existing',
  NEW_TANK_WITH_BASIC: 'subscription-tank-basic-new',
  UPGRADE_SINGLE_MULTI: 'subscription-single-to-multi',
  UPGRADE_BASIC_MULTI: 'subscription-basic-to-multi',
  ERROR: 'subscription-error',
  UPGRADE_LEGACY: 'subscription-legacy-upgrade'
}

const subscriptionDurations = {
  YEARLY: 'yearly',
  MONTHLY: 'monthly'
}

export default {
  props: ['modalId', 'apparatusId', 'apparatusList', 'accountProfile'],
  components: {
    PaymentModal,
    SubscriptionInfoError,
    UpgradeBasicToMulti,
    UpgradeApparatusesExistingSub,
    TankUtilityEnrollmentModals,
    EnrollmentModals,
    UpgradeFromLegacy,
    AddApparatusUpgradeSubscription
  },
  data() {
    return {
      activeView: null,
      productList: [],
      basicHsbList: null,
      existingSubscription: null,
      activeLegacySubscriptions: [],
      getSubscriptionInfoErrorMessage: null,
      isLoadingSubscriptionData: true,
      selectedProduct: null,
      showGetSubscriptionInfoError: false,
      subscriptionViews,
      isCreatingPurchaseIntent: false,
      apparatusesToAddToPlan: []
    }
  },
  watch: {
    apparatusId: function(newVal, oldVal) {
      if (!oldVal && newVal) {
        this.getSubscriptionInformation()
      }
    }
  },
  methods: {
    async savePremiumFeaturesGettingReadyInfo() {
      var subscriptionDetails = {}
      await this.$store.dispatch('getSubscriptionDetails', this.accountProfile.organizationId).then(response => {
        subscriptionDetails = response.data.subscriptions.find(
          sub =>
            subscriptionDetailActive(sub.status) &&
            !isDealerManaged(sub.productId) &&
            sub.apparatuses?.length > 0 &&
            subscriptionDetailContainsApparatus(sub.apparatuses, this.apparatusList[0].apparatusId) //since they have to upgrade all apparatus, we only need to check for one apparatus
        )
      })
      // if processing is complete, show toast
      if (
        subscriptionDetails &&
        (subscriptionDetails?.product.productId === this.selectedProduct.productId ||
          subscriptionDetails?.status === Constants.SubscriptionDetailPendingProductChange)
      ) {
        this.$store.dispatch('displayToast', {
          title: this.$t('xSuccess'),
          message: this.$t('xPlanChangedSuccessfully'),
          variant: 'success'
        })
        setTimeout(() => {
          window.location.reload()
        }, 3000)
      } else {
        // processing not complete, add each apparatus data to state to trigger notification banner
        await this.apparatusList.forEach(apparatus => {
          this.$store.dispatch('addPremiumFeaturesGettingReadyInfo', {
            apparatusId: apparatus.apparatusId,
            productSubscribedTo: this.selectedProduct.productId,
            timeStamp: Date.now()
          })
        })
        window.location.reload()
      }
    },
    async savePremiumFeaturesGettingNewApparatusListReadyInfo() {
      var subscriptionDetails = {}
      await this.$store.dispatch('getSubscriptionDetails', this.accountProfile.organizationId).then(response => {
        subscriptionDetails = response.data.subscriptions.find(
          sub =>
            subscriptionDetailActive(sub.status) &&
            sub.apparatuses?.length > 0 &&
            subscriptionDetailContainsApparatus(sub.apparatuses, this.apparatusList[0].apparatusId) //since they have to upgrade all apparatus, we only need to check for one apparatus
        )
      })
      // if processing is complete, show toast
      if (
        subscriptionDetails &&
        (subscriptionDetails?.product.productId === this.selectedProduct.productId ||
          subscriptionDetails?.status === Constants.SubscriptionDetailPendingProductChange)
      ) {
        this.$store.dispatch('displayToast', {
          title: this.$t('xSuccess'),
          message: this.$t('xPlanUpdated'),
          variant: 'success'
        })
        setTimeout(() => {
          window.location.reload()
        }, 3000)
      } else {
        // processing not complete, add each apparatus data to state to trigger notification banner
        await this.apparatusList.forEach(apparatus => {
          this.$store.dispatch('addPremiumFeaturesGettingReadyInfo', {
            apparatusId: apparatus.apparatusId,
            productSubscribedTo: this.selectedProduct.productId,
            timeStamp: Date.now()
          })
        })
        window.location.reload()
      }
    },
    /** This returns a list of eligible subscriptions and sets selectedProduct to the yearly version of the subscription */
    async getSubscriptionProduct() {
      const apparatusTypeToAddNonUnique = this.apparatusList
        .map(apparatus => Constants.ApparatusTypesAsString[apparatus.type])
        .join(',')
      // trim list to unique values
      const apparatusTypeToAdd = [...new Set(apparatusTypeToAddNonUnique.split(','))].join(',')
      var annualPaidProduct = null
      if (apparatusTypeToAdd) {
        await this.$store
          .dispatch('getSubscriptionProductsByStoreOrganization', {
            organizationId: this.accountProfile.organizationId
          })
          .then(getProductsResponse => {
            annualPaidProduct = this.getEligibleSubscription(getProductsResponse.data.products)
            if (!annualPaidProduct) {
              throw new Error()
            }
            this.selectedProduct = annualPaidProduct[1] //default to first yearly product
          })
          .catch(error => {
            // console.log('error', error)
          })
      }

      return annualPaidProduct
    },

    requireUpgrade() {
      if (!this.existingSubscription) {
        return true
      }

      const existingHSBCount = this.apparatusList.reduce((sum, app) => {
        if (app.type === Constants.HsbApparatusType) {
          return sum + 1
        }

        return sum
      }, 0)
      const existingTankCount = this.apparatusList.reduce((sum, app) => {
        if (app.type === Constants.FuelTankApparatusType) {
          return sum + 1
        }

        return sum
      }, 0)

      const appHSBString = Constants.ApparatusTypeToString[Constants.HsbApparatusType]
      const appTankString = Constants.ApparatusTypeToString[Constants.FuelTankApparatusType]

      const apparatusHSBLimit = this.existingSubscription.product?.apparatusTypesMax?.[appHSBString] ?? 0
      const apparatusTankLimit = this.existingSubscription.product?.apparatusTypesMax?.[appTankString] ?? 0

      return existingHSBCount > apparatusHSBLimit || existingTankCount > apparatusTankLimit
    },

    async getSubscriptionInformation() {
      this.productList = await this.getSubscriptionProduct()

      const getExistingSubscriptionsResponse = await this.$store.dispatch(
        'getSubscriptionDetails',
        this.accountProfile.organizationId
      )
      if (getExistingSubscriptionsResponse?.data?.subscriptions) {
        this.existingSubscription = getExistingSubscriptionsResponse?.data?.subscriptions.find(
          subscription =>
            subscription.store === Constants.WebSubscriptionStore &&
            subscription.status !== Constants.SubscriptionDetailCanceled
        )
        this.activeLegacySubscriptions = this.getActiveLegacySubscriptions(getExistingSubscriptionsResponse.data)
      }

      /** If the plan requires an upgrade and the existing subscription is monthly, need to update selectedProduct */
      if (this.requireUpgrade()) {
        if (this.existingSubscription?.product?.durationInMonths === 1 && this.productList.length > 0) {
          this.selectedProduct = this.productList[0] // Get monthly option
        }
      }

      if (this.existingSubscription && this.apparatusList) {
        /** Checks for non DM, non V1 tanks, and not within an existing subscription */
        this.apparatusesToAddToPlan = this.apparatusList.filter(
          apparatus =>
            !apparatus.isDealerManaged &&
            apparatus.properties?.find(x => x.type === Constants.ApparatusPropertyDevice)?.value?.deviceType !==
              Constants.TankUtilityLteDeviceTypeV1 &&
            apparatus.properties?.find(x => x.type === Constants.ApparatusPropertyDevice)?.value?.deviceType !==
              Constants.TankUtilityWifiDeviceType &&
            !this.existingSubscription?.apparatuses.some(x => x.apparatusId === apparatus.apparatusId)
        )
      }

      if (this.existingSubscription && !this.requireUpgrade()) {
        this.activeView = subscriptionViews.EXISTING_SUB
      } else if (this.existingSubscription && this.requireUpgrade()) {
        this.activeView = subscriptionViews.UPGRADE_SINGLE_MULTI
      } else {
        if (this.activeLegacySubscriptions?.length > 0) {
          this.activeView = subscriptionViews.UPGRADE_LEGACY
        } else {
          this.activeView = subscriptionViews.UPGRADE_BASIC_MULTI
        }
      }
      this.isLoadingSubscriptionData = false

      return
    },

    proceedToPayment() {
      if (this.selectedProduct && this.selectedProduct.cost > 0) {
        const apparatusIds = []
        this.apparatusList.forEach(apparatus => {
          apparatusIds.push(apparatus.apparatusId)
        })
        this.$store
          .dispatch('createPurchaseIntent', {
            productId: this.selectedProduct.productId,
            organizationId: this.accountProfile.organizationId,
            apparatusIds: apparatusIds
          })
          .then(() => {
            this.$bvModal.hide(this.modalId)
            this.$bvModal.show(`${this.modalId}-payment`)
          })
          .catch(() => {
            // send error back to payment modal - show this error and do not proceed
            reject(this.$t('xFailedSetUpGeneratorSubscription'))
          })
      } else if (this.selectedProduct && this.selectedProduct.cost === 0) {
        this.$bvModal.hide(this.modalId)
      }
      this.isCreatingPurchaseIntent = true
    },

    returnToSubscriptions() {
      this.$bvModal.hide(`${this.modalId}-payment`)
      this.$bvModal.show(this.modalId)
    },

    setSelectedProduct(product) {
      this.selectedProduct = product
    },

    payForProduct({ resolve, reject }) {
      if (this.accountProfile?.organizationId && this.selectedProduct?.productId && this.apparatusList) {
        // // note: when upgrading from SHP to MHP, we don't need to cancel anything - just create purchase intent and update sub product
        const apparatusIds = []
        this.apparatusList.forEach(apparatus => {
          apparatusIds.push(apparatus.apparatusId)
        })

        const changeSubscriptionMode = {
          organizationId: this.accountProfile.organizationId,
          productId: this.selectedProduct.productId
        }

        this.$store
          .dispatch('createPurchaseIntent', {
            productId: this.selectedProduct.productId,
            organizationId: this.accountProfile.organizationId,
            apparatusIds: apparatusIds
          })
          .then(() => {
            this.$store
              .dispatch('updateSubscriptionProduct', changeSubscriptionMode)
              .then(response => {
                this.isCreatingPurchaseIntent = false
                // send back success to the payment modal
                resolve(response)
                this.$bvModal.hide(`${this.modalId}-payment`)
              })
              .catch(error => {
                this.isCreatingPurchaseIntent = false
                let failureMessage = this.$t('xFailedSetUpGeneratorSubscription')
                if (error.response?.data?.message) {
                  try {
                    let messageAsJson = JSON.parse(error.response.data.message.replace(/\\"/g, '"'))
                    if (messageAsJson?.gatewayResponse?.message) {
                      failureMessage = messageAsJson.gatewayResponse.message
                    }
                  } catch (err) {}
                }
                // send error back to payment modal - show this error and do not proceed
                reject(failureMessage)
              })
          })
          .catch(error => {
            // send error back to payment modal - show this error and do not proceed
            reject(this.$t('xFailedSetUpGeneratorSubscription'))
          })
      }
    },

    async updateExistingProductApparatuses() {
      this.$bvModal.hide(this.modalId)
      this.openModal('update-apparatuses-spinner-modal')
      const apparatusIds = []
      this.apparatusList.forEach(apparatus => {
        apparatusIds.push(apparatus.apparatusId)
      })

      await this.$store
        .dispatch('updateSubscriptionApparatuses', {
          organizationId: this.accountProfile.organizationId,
          store: this.existingSubscription?.store,
          productId: this.existingSubscription?.product.productId,
          apparatusIds: apparatusIds
        })
        .then(() => {
          this.savePremiumFeaturesGettingNewApparatusListReadyInfo()
        })
        .catch(() => {
          this.$store.dispatch('displayToast', {
            title: this.$t('xSomethingWentWrong'),
            message: this.$t('xPleaseWaitAndTryAgain'),
            variant: 'danger'
          })
        })
        .finally(() => {
          this.activeView = null
          this.closeModal('update-apparatuses-spinner-modal')
        })
    },

    getEligibleSubscription(products) {
      if (!this.apparatusList) {
        return null
      }

      const apparatusTypeCounts = this.apparatusList?.reduce(
        (ctr, app) => {
          const newCtr = { ...ctr }
          if (app.isDealerManaged) {
            return newCtr // dealer managed don't count toward subscription eligibility
          }

          if (app.type === Constants.HsbApparatusType) {
            newCtr.hsb = ctr.hsb + 1
          } else if (app.type === Constants.FuelTankApparatusType) {
            // v1 tanks aren't added to subscriptions, nor do they prevent a user from having a single-home subscription
            // HOWEVER, they are included when determining if there are 6 apparatuses (the max) on the account, because of course they are
            if (isFuelMonitorV2(app)) {
              newCtr.tank = ctr.tank + 1
            } else {
              newCtr.tankV1 = ctr.tankV1 + 1
            }
          }

          return newCtr
        },
        {
          hsb: 0,
          tank: 0,
          tankV1: 0
        }
      )

      const apparatusCount = apparatusTypeCounts.hsb + apparatusTypeCounts.tank

      if (apparatusCount + apparatusTypeCounts.tankV1 >= 6) {
        return null
      }

      return products.filter(product => {
        if (apparatusCount > product.maxApparatuses) {
          return false
        }

        return (
          apparatusTypeCounts.hsb <= (product.apparatusTypesMax.Generator ?? 0) &&
          apparatusTypeCounts.tank <= (product.apparatusTypesMax.FuelTank ?? 0)
        )
      })
    },
    getActiveLegacySubscriptions(subscriptionData) {
      if (!subscriptionData) {
        return []
      }

      return subscriptionData.legacyBillingAccount?.products?.filter(
        product =>
          product.isLegacy && // is a legacy sub that...
          subscriptionActive(product) && // is active, but is NOT...
          !product.productId.includes('free-wifi') && // a free sub,...
          !(
            product.productId === Constants.DealerManagedProductId ||
            product.productId === Constants.TankUtilityDealerManagedProductId
          ) && // dealer managed, or...
          !this.apparatusList // a v1 tank
            .find(apparatus => apparatus.apparatusId === product.apparatusId)
            ?.properties.find(property => property.name === 'Device' && property.deviceType?.includes('tankutility-v1'))
      )
    }
  },
  computed: {
    modalSize() {
      switch (this.activeView) {
        case subscriptionViews.NEW_HSB:
        case subscriptionViews.UPGRADE_SINGLE_MULTI:
          return 'lg'
        default:
          return 'md'
      }
    },
    modalTitle() {
      switch (this.activeView) {
        case subscriptionViews.EXISTING_SUB:
          return this.$t('xUpdatePlan')
        case subscriptionViews.UPGRADE_SINGLE_MULTI:
        case subscriptionViews.UPGRADE_LEGACY:
          return this.$t('xUpgradeYourPlan')
        case subscriptionViews.UPGRADE_BASIC_MULTI:
          return this.$t('xUpgradePremiumMonitoring')
        default:
          return this.$t('xSubscribeToPremium')
      }
    }
  }
}
</script>
<style lang="scss">
.legal-text {
  font-size: 0.875em;
  color: var(--gray-600);
}
</style>
